import axios from 'axios';
import { getCustomerApiBaseURL } from '../utils/apiConfig';

const api = axios.create({
  baseURL: getCustomerApiBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Don't add token for public auth routes, collaborator portal and anonymous whistleblowing
const publicRoutes = [
  '/auth/client/login',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/change-password',
  '/auth/2fa/verify',
  '/auto-agendamento',
  '/portal-colaborador',
  '/customer/presenca',
  '/presenca',
  '/denuncias-anonimas',
  '/public/domain/environment-banner',
  '/public/',
];

/**
 * Verifica se a URL atual do navegador é de uma rota pública
 * (autenticação, portal do colaborador, autoagendamento, etc.)
 */
export function isPublicPath(pathname: string): boolean {
  if (pathname.startsWith('/colaboradores')) {
    return false;
  }
  return (
    pathname === '/login' ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/change-password') ||
    pathname.startsWith('/escolha-clinica') ||
    pathname === '/colaborador' ||
    pathname.startsWith('/colaborador/') ||
    pathname === '/colab' ||
    pathname.startsWith('/colab/') ||
    pathname.startsWith('/portal-colaborador') ||
    pathname.startsWith('/confirmar-presenca') ||
    pathname.startsWith('/presenca') ||
    pathname.startsWith('/avaliar') ||
    pathname.startsWith('/feedback') ||
    pathname.startsWith('/epi/aceite') ||
    pathname.startsWith('/parceiro') ||
    pathname.startsWith('/portal-parceiro') ||
    pathname.startsWith('/upload') ||
    pathname.startsWith('/clinica')
  );
}

// Interceptor para adicionar token de autenticação e empresa_id
api.interceptors.request.use((config) => {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicPage = isPublicPath(currentPath);

  const isPublicRoute = publicRoutes.some(route => {
    if (route === '/auto-agendamento') {
      return (
        !config.url?.includes('/agendamentos/') &&
        (config.url?.startsWith('/auto-agendamento') || config.url?.startsWith('/customer/auto-agendamento'))
      );
    }
    return config.url?.includes(route);
  });

  // Em rotas públicas temporárias ou páginas de autenticação pública, nunca vincular sessão de usuário logado
  if (!isPublicRoute && !isPublicPage) {
    const token = localStorage.getItem('customer_token');

    // Log detalhado APENAS se a requisição falhar (poderíamos logar sempre, mas gera ruído)
    // Mas para debug atual, vamos logar sempre que for /users/me ou /features
    if (config.url?.includes('/users/me') || config.url?.includes('/features')) {
      console.log(`🔍 [API Debug] Request to ${config.url}`, {
        isPublicRoute,
        hasToken: !!token,
        tokenStart: token ? `${token.substring(0, 10)}...` : 'null',
        headers: config.headers,
        baseURL: config.baseURL
      });
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['x-forwarded-authorization'] = `Bearer ${token}`;
    }

    // Add empresa_id to header if available
    const empresaId = localStorage.getItem('customer_empresa_id');
    if (empresaId) {
      config.headers['x-empresa-id'] = empresaId;
    }
  }

  return config;
});

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Retry on 425 Too Early (TLS 0-RTT replay protection)
    if (error.response?.status === 425 && error.config && !error.config._retry) {
      console.warn('⚠️ [API] 425 Too Early - Retrying request...', error.config.url);
      error.config._retry = true;
      try {
        return api.request(error.config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

    // Log de erro detalhado para 401
    if (error.response?.status === 401) {
      console.error('❌ [API Error] 401 Unauthorized:', {
        url: error.config?.url,
        hasAuthHeader: !!error.config?.headers?.Authorization,
        isPublicRoute: publicRoutes.some(route => error.config?.url?.includes(route))
      });

      // Token expirado ou inválido, limpar dados residuais da sessão
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_user');
      localStorage.removeItem('customer_empresas');
      localStorage.removeItem('customer_empresa_id');

      // Só redirecionar se não estiver em uma rota pública (login, reset-password, colaborador, etc.)
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      if (!isPublicPath(currentPath)) {
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403 && typeof window !== 'undefined') {
      const msg = String(error.response?.data?.message || '');
      if (msg.toLowerCase().includes('acesso a esta empresa')) {
        window.dispatchEvent(new CustomEvent('REFRESH_CLIENT_EMPRESAS'));
      }
    }
    return Promise.reject(error);
  }
);

export default api;
